import { useState } from 'react';
import { toast } from 'react-toastify';
import { FilterDateRange, useDateRange, formatPeriodLabel } from './useDateRange';
import { localCacheDB, CachedData, cachedListKey, purgeUnscopedCaches } from '../db/localCache';
import { useAuth } from './useAuth';
import { useTranslation } from 'react-i18next';
import { readStoredDateRange } from '../util/sessionFilters';

export const useData = <T>(localStorageKey?: string) => {
    const [data, setData] = useState<T[]>([]);
    const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
    const [loading, setLoading] = useState<boolean>();

    // Shares the page's stored date range choice (DateRangeFilter dateRangeKey = localStorageKey).
    const { dateRange, setDateRange } = useDateRange(localStorageKey);
    const {t} = useTranslation();
    const { session } = useAuth();

    // Rows are filtered by the signed-in user's data scope, so one user's cache
    // must never be shown to another on the same browser.
    const cacheKey = cachedListKey(localStorageKey, session?.user_id);


    // 🔹 Load from IndexedDB
    const loadFromIndexedDB = async (): Promise<boolean> => {
        if (!cacheKey) return false;

        setLoading(true);

        try {
            await purgeUnscopedCaches();
            const cached = await localCacheDB.records.get(cacheKey);

            if (cached && cached.data?.length > 0) {
                setData(cached.data as T[]);
                setLastUpdate(new Date(cached.lastUpdate));

                // A range chosen this session wins: it is what the next refresh asks for.
                if (cached.period && !readStoredDateRange(localStorageKey)) {
                    setDateRange({
                        from: cached.period.from ? new Date(cached.period.from) : null,
                        to: cached.period.to ? new Date(cached.period.to) : null,
                    });
                }

                setLoading(false);
                return true;
            }
        } catch (error) {
            console.error("Failed to read from IndexedDB:", error);
        }

        setLoading(false);
        return false;
    };

    // 🔹 Save to IndexedDB
    const saveFromServer = async (
        data: any,
        period?: FilterDateRange,
        forceUpdate?: boolean
    ): Promise<void> => {
        if (data.length === 0) {
            const key = forceUpdate
                ? (period ? "common:toast.warning.noDataWithPeriodForced" : "common:toast.warning.noDataForced")
                : (period ? "common:toast.warning.noDataWithPeriod" : "common:toast.warning.noData");
            toast.warn(t(key, { period: period ? formatPeriodLabel(period) : undefined }));
        }

        // An empty answer is an answer: keeping the previous rows on screen (and
        // in the cache) is what made deleted or out-of-scope records linger.
        setData(data);

        if (!cacheKey) {
            console.info("Missing storage key, data will not cache!");
            return;
        }

        const now = new Date();

        const payload: CachedData = {
            key: cacheKey,
            data,
            lastUpdate: now.toISOString(),
            ...(period && {
                period: {
                    from: period.from ? period.from.toISOString() : null,
                    to: period.to ? period.to.toISOString() : null,
                },
            })
        };

        try {
            await localCacheDB.records.put(payload);
        } catch (e: any) {
            console.error("Unexpected error saving to IndexedDB:", e);
            toast.warn(t("common:toast.warning.idbIssue"));
        } finally {
            setLastUpdate(now);
            if (period) {
                setDateRange(period);
            }
        }
    };

    return { data, lastUpdate, setData, setLastUpdate, loadFromIndexedDB, saveFromServer, loading, setLoading, dateRange, setDateRange };
};
